import { useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"

// Opens the OS file picker and uploads the chosen file as a piece attachment.
// Replaces the old Dropbox Chooser: bytes are POSTed (multipart) to
// /pieces/:id/attachments and stored in Active Storage. Same prop shape as the
// button it replaced, so it drops into the existing attachment UI unchanged.

type Kind = "image" | "video" | "featured_image"

const ACCEPT: Record<Kind, string> = {
  image: "image/*",
  featured_image: "image/*",
  video: "video/*",
}

const DEFAULT_LABEL: Record<Kind, string> = {
  image: "Add image",
  featured_image: "Pick featured image",
  video: "Add video",
}

type Props = {
  pieceId: number
  kind: Kind
  label?: string
}

export function FileUploadButton({ pieceId, kind, label }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    router.post(
      `/pieces/${pieceId}/attachments`,
      { attachment: { kind, file } },
      {
        preserveScroll: true,
        forceFormData: true,
        onStart: () => setUploading(true),
        onFinish: () => {
          setUploading(false)
          // Clear the input so re-picking the same file fires onChange again.
          if (inputRef.current) inputRef.current.value = ""
        },
      },
    )
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT[kind]}
        className="hidden"
        onChange={onChange}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? "Uploading…" : (label ?? DEFAULT_LABEL[kind])}
      </Button>
    </>
  )
}
